import React, {MouseEvent} from "react";
import {autobind} from "core-decorators";
import {action, computed, observable} from "mobx";
import {inject, observer} from "mobx-react";
import {Badge, Button, message, Modal, Radio, RadioChangeEvent, Space, Table} from "antd";
import {CheckOutlined, StopOutlined} from "@ant-design/icons";
import pluralize from "pluralize";

import "./SubscriptionPricingTable.scss";
import {paths} from "@/routes";
import {
  BILLING_PERIOD_MONTHLY,
  BILLING_PERIOD_YEARLY,
  INITIAL_SCORE_CREDITS,
  Plans,
  type BillingPeriod,
  type PlanName,
  type Pricing,
} from "@/shared/plans";
import {getPrice} from "./prices";
import {getCheckoutPage, startTrial} from "@/lib/backend";
import loadChargebee, {getChargebeeInstance} from "@/lib/chargebee/loadChargebee";
import Store from "@/stores";

export interface ISubscriptionPricingTableProps {
  store?: Store;
}

@loadChargebee
@inject("store")
@autobind
@observer
export default class SubscriptionPricingTable extends React.Component<ISubscriptionPricingTableProps> {
  @observable billingPeriod: BillingPeriod = BILLING_PERIOD_MONTHLY;
  @observable startingTrial: boolean = false;

  @computed({keepAlive: true})
  get chargebee() {
    const {stageStore} = this.props.store!;
    return getChargebeeInstance(stageStore.stage.chargebeeSite);
  }

  @action
  billingPeriodChanged(event: RadioChangeEvent) {
    this.billingPeriod = event.target.value;
  }

  getPrice(price: Pricing, lineBreak = true, showMonthlyForYearly = true) {
    return getPrice(price, this.billingPeriod, lineBreak, showMonthlyForYearly);
  }

  handleBasicSubscribeClick(_event: MouseEvent<HTMLElement>) {
    this.openCheckout("Basic");
  }

  handleEnsembleSubscribeClick(_event: MouseEvent<HTMLElement>) {
    this.openCheckout("Ensemble", 0);
  }

  handleProSubscribeClick(_event: MouseEvent<HTMLElement>) {
    this.openCheckout("Pro");
  }

  async handleStartTrialClick() {
    this.setStartingTrial(true);
    let errorMessage;
    try {
      await startTrial();
      this.trialStarted();
    } catch (error) {
      errorMessage = error?.toJSON?.()?.error ?? "Unknown error";
      Modal.error({title: "Cannot start trial", content: errorMessage});
    } finally {
      this.setStartingTrial(false);
    }
  }

  async openCheckout(plan: PlanName, extraSingers: number | undefined = undefined) {
    const {history} = this.props.store!;
    this.chargebee.openCheckout({
      hostedPage: async () => {
        const user = this.props.store!.session.voicefulUser!;
        return await getCheckoutPage({
          name: user.name,
          plan,
          billingPeriod: this.billingPeriod,
          extraSingers,
        });
      },
      error: (e: any) => {
        if (e === "Already another checkout is in progress") {
          // Bug in Chargebee, this can occur sometimes and it's harmless.
          console.log(e);
          return;
        }
        let error = e.responseJSON?.error;
        if (!error) error = e.status ? `status code ${e.status}` : "Unknown error";
        message.error(`Error opening subscription page: ${error}`);
      },
      success: (hostedPageId: string) => {
        console.log("Checkout succeeded. hostedPageId: " + hostedPageId);
        this.chargebee.closeAll();
        history.history.push(paths.subscriptionProcessing());
      },
      // loaded: function() {
      //   console.log("checkout opened");
      // },
      // close: function() {
      //   console.log("checkout closed");
      // },
      // step: function(checkoutStep: string) {
      //   console.log(checkoutStep);
      // },
    });
  }

  @computed({keepAlive: true})
  get pricingTableData() {
    return [
      {
        title: "Price",
        basic: (
          <>
            <Button type="primary" onClick={this.handleBasicSubscribeClick} className="subscribe-btn">
              Subscribe
            </Button>
            <div>{this.getPrice(Plans.Basic.pricing)}</div>
          </>
        ),
        pro: (
          <>
            <Button type="primary" onClick={this.handleProSubscribeClick} className="subscribe-btn">
              Subscribe
            </Button>
            <div>{this.getPrice(Plans.Pro.pricing)}</div>
          </>
        ),
        ensemble: (
          <Space align="start">
            <div>
              <Button
                type="primary"
                danger
                onClick={this.handleStartTrialClick}
                loading={this.startingTrial}
                className="subscribe-btn"
              >
                Start Free Trial
              </Button>
              <div>
                Free for 14 days
                <br />
                No credit card required
              </div>
            </div>
            <div>
              <Button type="primary" onClick={this.handleEnsembleSubscribeClick} className="subscribe-btn">
                Subscribe
              </Button>
              <div>{this.getPrice(Plans.Ensemble.pricing)}</div>
            </div>
          </Space>
        ),
      },
      {
        title: "User profile",
        basic: "Individual or small ensemble singers who want to practice using interactive scores with lyrics",
        pro: "Singers, composers or conductors of small ensembles who want to generate practice tracks for their scores",
        ensemble:
          "Ensemble conductors (+20 members) who want an interactive rehearsal tool for their singers with custom repertoire",
      },
      {
        title: "Access to free catalog",
        basic: <CheckOutlined />,
        pro: <CheckOutlined />,
        ensemble: <CheckOutlined />,
      },
      {
        title: "Practice and record your parts",
        basic: <CheckOutlined />,
        pro: <CheckOutlined />,
        ensemble: <CheckOutlined />,
      },
      {
        title: "Get automatic feedback on your performance",
        basic: <CheckOutlined />,
        pro: <CheckOutlined />,
        ensemble: <CheckOutlined />,
      },
      {
        title: "Be invited to multiple ensembles",
        basic: <CheckOutlined />,
        pro: <CheckOutlined />,
        ensemble: <CheckOutlined />,
      },
      {
        title: "Create multiple ensembles",
        basic: <StopOutlined />,
        pro: "Unlimited",
        ensemble: "Unlimited",
      },
      {
        title: "Invite singers to your ensembles",
        basic: <StopOutlined />,
        pro: "Only subscribed users (unlimited singers)",
        ensemble: `Includes ${pluralize(
          "ensemble member account",
          Plans.Ensemble.includedMembers,
          true,
        )}. Additional member for ${Plans.Ensemble.extraSinger.Monthly} €/month (${
          Plans.Ensemble.extraSinger.Yearly
        } €/year)`,
      },
      {
        title: "Assign conductor profiles for your ensembles",
        basic: <StopOutlined />,
        pro: <CheckOutlined />,
        ensemble: <CheckOutlined />,
      },
      {
        title: "Conductors can access to shared recordings and give feedback",
        basic: <StopOutlined />,
        pro: <CheckOutlined />,
        ensemble: <CheckOutlined />,
      },
      {
        title: "Upload custom scores",
        basic: <StopOutlined />,
        pro: `${INITIAL_SCORE_CREDITS} (sign-up) + ${Plans.Pro.scoreCredits.Yearly} per year (${Plans.Pro.scoreCredits.Monthly} per month)`,
        ensemble: `${INITIAL_SCORE_CREDITS} (sign-up) + ${Plans.Ensemble.scoreCredits.Yearly} per year (${Plans.Ensemble.scoreCredits.Monthly} per month)`,
      },
      {
        title: "Download rendered practice tracks",
        basic: <StopOutlined />,
        pro: <CheckOutlined />,
        ensemble: <CheckOutlined />,
      },
    ];
  }

  @action
  setStartingTrial(startingTrail: boolean) {
    this.startingTrial = startingTrail;
  }

  trialStarted() {
    Modal.info({
      title: "Your free trial has started!",
      content: (
        <>
          <p>
            You can now start using Cantāmus by adding pieces (on the My Repertoire page) and inviting ensemble members
            (on the Ensemble page).
          </p>
          <p>At any time you can upgrade or cancel your trial on the Subscription page.</p>
        </>
      ),
      onOk: async () => {
        const {history, session} = this.props.store!;
        await session.fetchSessionData();
        history.history.push(paths.home());
      },
    });
  }

  render() {
    return (
      <div className="SubscriptionPricingTable">
        <div className="billing-period">
          <Radio.Group buttonStyle="solid" value={this.billingPeriod} onChange={this.billingPeriodChanged}>
            <Radio.Button value={BILLING_PERIOD_MONTHLY}>1 month</Radio.Button>
            <Badge count="save 16%" offset={[-10, 38]}>
              <Radio.Button value={BILLING_PERIOD_YEARLY}>12 months</Radio.Button>
            </Badge>
          </Radio.Group>
        </div>
        <Table className="pricing-table" dataSource={this.pricingTableData} pagination={false}>
          <Table.Column title="" dataIndex="title" />
          <Table.Column
            title={
              <>
                <strong>Basic</strong> plan
              </>
            }
            dataIndex="basic"
            align="center"
          />
          <Table.Column
            title={
              <>
                <strong>Pro</strong> plan
              </>
            }
            dataIndex="pro"
            align="center"
          />
          <Table.Column
            title={
              <>
                <strong>Ensemble</strong> plan
              </>
            }
            dataIndex="ensemble"
            align="center"
          />
        </Table>
      </div>
    );
  }
}
